import { Marker } from 'maplibre-gl';
import { createRoot, type Root } from 'react-dom/client';
import { ThemeProvider, type DefaultTheme } from 'styled-components';

import { getLanguageDirection } from '@utils/languageDirection.ts';
import modDefaultTheme from '@utils/modDefaultTheme.tsx';
import { NAV_BADGE_LAYER_ID } from '@utils/routeMarkerIds.ts';

import type {
  TransitLocationReference,
  PreviewInsets,
  PreviewMilestone,
  PreviewPoi,
  ShowRoutePreviewPayload,
} from '../../../../src/types/route-preview.ts';
import type { MapInitialized, MapRendererApp } from '../types.ts';
import { buildMilestones } from './buildMilestones.ts';
import {
  getCalloutPlacement,
  PREVIEW_BADGE_SIZE,
  DESTINATION_PIN_WIDTH,
  DESTINATION_PIN_HEIGHT,
  DESTINATION_PIN_TIP_Y,
  getPreviewBadgeSize,
} from './calloutPlacement.ts';
import { applyCalloutPlacement, placeCallouts, type CalloutSide, type CollisionItem } from './collisionPlacement.ts';
import RoutePreviewMarker from './RoutePreviewMarker.tsx';

type OwnedMarker = {
  instance: Marker;
  element: HTMLElement;
  root: Root;
  resize: ResizeObserver;
  mutation: MutationObserver;
};

/** Owns only preview markers. The preview UI explicitly supplies the selected whole route. */
export default function RoutePreviewController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  fullRoutePreviewEnabled = false,
) {
  if (!fullRoutePreviewEnabled) {
    return {};
  }

  const log = app.log.sublog('routePreviewBadges');
  const markers = new Map<string, OwnedMarker>();
  const previousLayerVisibility = new Map<string, 'visible' | 'none' | undefined>();
  let milestones: PreviewMilestone[] = [];
  let payload: ShowRoutePreviewPayload | undefined;
  let latestRequestedPayload: ShowRoutePreviewPayload | undefined;
  let latestInsets: PreviewInsets | undefined;
  let generation = 0;
  let disposed = false;
  let active = false;
  let placementFrame: number | undefined;
  let placementNeeded = false;
  let map: Awaited<ReturnType<MapInitialized>> | undefined;
  const direction = () => getLanguageDirection(app.i18n?.().language ?? 'en');
  const resizeBadges = () => {
    const size = getPreviewBadgeSize(map?.getZoom?.() ?? 16);
    const scale = size / PREVIEW_BADGE_SIZE;
    for (const milestone of milestones) {
      const owned = markers.get(milestone.id);
      if (!owned || owned.element.style.getPropertyValue('--preview-badge-scale') === String(scale)) {
        continue;
      }
      const destination = milestone.kind === 'destination';
      owned.element.style.setProperty('--preview-badge-scale', String(scale));
      if (destination) {
        owned.instance.setOffset([0, (DESTINATION_PIN_HEIGHT - DESTINATION_PIN_TIP_Y) * scale]);
      }
    }
  };

  const removeMarker = (id: string, marker: OwnedMarker) => {
    marker.resize.disconnect();
    marker.mutation.disconnect();
    marker.instance.remove();
    marker.root.unmount();
    markers.delete(id);
  };
  const removeMarkers = () => {
    for (const [id, marker] of markers) {
      removeMarker(id, marker);
    }
    markers.clear();
  };

  const restoreNavBadges = () => {
    if (map) {
      for (const [id, visibility] of previousLayerVisibility) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, 'visibility', visibility ?? 'visible');
        }
      }
    }
    previousLayerVisibility.clear();
  };

  const hideNavBadges = () => {
    if (!map?.getLayer(NAV_BADGE_LAYER_ID)) {
      return;
    }
    if (!previousLayerVisibility.has(NAV_BADGE_LAYER_ID)) {
      previousLayerVisibility.set(
        NAV_BADGE_LAYER_ID,
        map.getLayoutProperty(NAV_BADGE_LAYER_ID, 'visibility') as 'visible' | 'none' | undefined,
      );
    }
    if (map.getLayoutProperty(NAV_BADGE_LAYER_ID, 'visibility') !== 'none') {
      map.setLayoutProperty(NAV_BADGE_LAYER_ID, 'visibility', 'none');
    }
  };

  const positionCallouts = () => {
    if (!map) {
      return;
    }
    resizeBadges();
    const currentMap = map;
    const container = map.getContainer();
    const mapPadding = map.getPadding();
    const insets = { ...mapPadding, ...latestInsets };
    const viewport = { width: container.clientWidth, height: container.clientHeight };
    const scale = getPreviewBadgeSize(map.getZoom?.() ?? 16) / PREVIEW_BADGE_SIZE;
    const layouts = milestones.flatMap(milestone => {
      const owned = markers.get(milestone.id);
      if (!owned) {
        return [];
      }
      const point = currentMap.project(milestone.coordinates);
      const callout = owned.element.querySelector<HTMLElement>('[data-preview-callout]');
      const badgeWidth = (milestone.kind === 'destination' ? DESTINATION_PIN_WIDTH : PREVIEW_BADGE_SIZE) * scale;
      const anchorY = (milestone.kind === 'destination' ? DESTINATION_PIN_TIP_Y : PREVIEW_BADGE_SIZE / 2) * scale;
      const badgeHeight = (milestone.kind === 'destination' ? DESTINATION_PIN_HEIGHT : PREVIEW_BADGE_SIZE) * scale;
      const calloutPoint = { x: point.x, y: point.y + badgeHeight / 2 - anchorY };
      const space = getCalloutPlacement(calloutPoint, viewport, insets, undefined, undefined, badgeWidth);
      return [{ owned, point, calloutPoint, callout, space, badgeWidth, badgeHeight, kind: milestone.kind }];
    });
    // Apply constraints to all labels first, then read their dimensions as a single layout phase.
    for (const { owned, space } of layouts) {
      owned.element.style.setProperty('--preview-callout-width', `${space.maxWidth}px`);
    }
    const measuredLayouts = layouts.map(layout => ({
      ...layout,
      height: layout.callout?.offsetHeight,
      width: layout.callout?.offsetWidth,
    }));
    for (const { owned, calloutPoint, callout, height, width, badgeWidth, badgeHeight } of measuredLayouts) {
      const placement = getCalloutPlacement(calloutPoint, viewport, insets, height, width, badgeWidth);
      const isInlineStart = direction() === 'rtl' ? placement.side === 'right' : placement.side === 'left';
      owned.element.dataset.calloutSide = isInlineStart ? 'inline-start' : 'inline-end';
      owned.element.style.setProperty('--preview-callout-y', `${placement.y + badgeHeight / 2}px`);
      owned.element.style.setProperty('--preview-callout-translate-y', '0px');
      if (callout) {
        callout.style.visibility = '';
      }
    }
    // Hidden anchors must not reserve label space or displace visible callouts.
    const collisionLayouts = measuredLayouts.filter(
      ({ point }) => point.x >= 0 && point.x <= viewport.width && point.y >= 0 && point.y <= viewport.height,
    );
    const items: CollisionItem[] = collisionLayouts.map(
      ({ owned, point, height, width, badgeWidth, badgeHeight, kind }) => ({
        required: kind === 'origin' || kind === 'destination',
        id: owned.element.querySelector<HTMLElement>('[data-preview-milestone]')?.dataset.previewMilestone ?? '',
        badge: {
          x: point.x - badgeWidth / 2,
          y: point.y - (kind === 'destination' ? DESTINATION_PIN_TIP_Y * scale : badgeHeight / 2),
          width: badgeWidth,
          height: badgeHeight,
        },
        ...(height && width ? { label: { height, width } } : {}),
        preferred: (['left', 'right', 'above', 'below'].includes(owned.element.dataset.collisionSide ?? '')
          ? owned.element.dataset.collisionSide
          : 'right') as CalloutSide,
      }),
    );
    const placements = placeCallouts(items, {
      x: 8,
      y: 8,
      width: Math.max(0, viewport.width - 16),
      height: Math.max(0, viewport.height - 16),
    });
    placements.forEach(placement => {
      const index = items.findIndex(item => item.id === placement.id);
      const element = collisionLayouts[index].owned.element;
      // The marker owns the coordinate system and the callout/leader are its children.
      applyCalloutPlacement(element, placement, items[index].badge);
    });
    updateVisibility(layouts);
  };

  const updateVisibility = (projected?: { owned: OwnedMarker; point: { x: number; y: number } }[]) => {
    if (!map) {
      return;
    }
    const currentMap = map;
    const container = currentMap.getContainer();
    const visibleMarkers =
      projected ??
      milestones.flatMap(milestone => {
        const owned = markers.get(milestone.id);
        if (!owned) {
          return [];
        }
        return [{ owned, point: currentMap.project(milestone.coordinates) }];
      });
    for (const { owned, point } of visibleMarkers) {
      // Panels naturally cover markers; only anchors outside the actual map are hidden.
      owned.element.style.visibility =
        point.x < 0 || point.x > container.clientWidth || point.y < 0 || point.y > container.clientHeight
          ? 'hidden'
          : 'visible';
    }
  };
  const scheduleVisibility = () => {
    if (placementFrame === undefined && map && !disposed) {
      placementFrame = requestAnimationFrame(() => {
        placementFrame = undefined;
        if (placementNeeded) {
          placementNeeded = false;
          positionCallouts();
        } else {
          updateVisibility();
        }
      });
    }
  };
  const scheduleCallouts = () => {
    placementNeeded = true;
    scheduleVisibility();
  };

  const renderVisible = () => {
    if (!map || !payload || disposed) {
      return;
    }
    const ids = new Set(milestones.map(milestone => milestone.id));
    for (const [id, owned] of markers) {
      if (!ids.has(id)) {
        removeMarker(id, owned);
      }
    }
    hideNavBadges();
    for (const milestone of milestones) {
      let owned = markers.get(milestone.id);
      if (!owned) {
        const element = document.createElement('div');
        element.setAttribute('aria-hidden', 'true');
        element.style.pointerEvents = 'none';
        const destination = milestone.kind === 'destination';
        element.style.width = `calc(${destination ? DESTINATION_PIN_WIDTH : PREVIEW_BADGE_SIZE}px * var(--preview-badge-scale, 1))`;
        element.style.height = `calc(${destination ? DESTINATION_PIN_HEIGHT : PREVIEW_BADGE_SIZE}px * var(--preview-badge-scale, 1))`;
        const root = createRoot(element);
        const resize = new ResizeObserver(scheduleCallouts);
        resize.observe(element);
        const mutation = new MutationObserver(() => {
          const callout = element.querySelector<HTMLElement>('[data-preview-callout]');
          if (callout) {
            resize.observe(callout);
          }
          scheduleCallouts();
        });
        mutation.observe(element, { childList: true, subtree: true });
        const instance = new Marker({
          element,
          anchor: destination ? 'bottom' : 'center',
          ...(destination ? { offset: [0, DESTINATION_PIN_HEIGHT - DESTINATION_PIN_TIP_Y] as [number, number] } : {}),
        })
          .setLngLat(milestone.coordinates)
          .addTo(map);
        owned = { instance, element, root, resize, mutation };
        markers.set(milestone.id, owned);
      }
      owned.instance.setLngLat(milestone.coordinates);
      owned.root.render(
        <ThemeProvider theme={(app.themePack?.getTheme() ?? modDefaultTheme) as DefaultTheme}>
          <RoutePreviewMarker milestone={milestone} direction={direction()} />
        </ThemeProvider>,
      );
    }
    scheduleCallouts();
  };

  const clear = () => {
    if (placementFrame !== undefined) {
      cancelAnimationFrame(placementFrame);
    }
    placementFrame = undefined;
    placementNeeded = false;
    generation++;
    payload = undefined;
    latestRequestedPayload = undefined;
    latestInsets = undefined;
    milestones = [];
    removeMarkers();
    restoreNavBadges();
    if (active) {
      void app.bus.send('map/setNavigationMarkersVisible', { visible: true });
    }
    active = false;
  };

  const show = async (next: ShowRoutePreviewPayload) => {
    latestRequestedPayload = next;
    const request = ++generation;
    latestInsets = next.insets ?? latestInsets;
    let replacementStarted = false;
    const poiCache = new Map<string | number, Promise<PreviewPoi | undefined>>();
    const optionalLookup = async <T,>(description: string, lookup: () => Promise<T>) => {
      try {
        return await lookup();
      } catch (error) {
        log.error(`Unable to resolve optional preview ${description}`, error);
        return undefined;
      }
    };
    const poi = (id: string | number) => {
      let result = poiCache.get(id);
      if (!result) {
        result = optionalLookup(`POI ${id}`, () =>
          app.bus.getFirst<{ id: string | number }, PreviewPoi>('poi/getById', { id }),
        );
        poiCache.set(id, result);
      }
      return result;
    };
    const resolveLocation = async (reference: TransitLocationReference) => {
      // Waypoint POI ids can identify the transit service; floor context names the ride's locations.
      if (reference.floorId) {
        const originalFloorId = reference.floorId;
        const floorId = await optionalLookup<string | undefined>('floor id', () =>
          app.bus.getFirst<{ floorId: string }, string>('venueData/getTranslatedFloorId', {
            floorId: originalFloorId,
          }),
        );
        const names = await optionalLookup<{ structureName?: string } | undefined>('floor name', () =>
          app.bus.getFirst<{ floorId: string }, { structureName?: string }>('venueData/getFloorIdName', {
            floorId: floorId ?? originalFloorId,
          }),
        );
        if (names?.structureName) {
          return names.structureName;
        }
      }
      if (reference.poiId != null) {
        const named = await poi(reference.poiId);
        if (named?.name) {
          return named.name;
        }
      }
      return undefined;
    };
    const locationCache = new Map<string, Promise<string | undefined>>();
    const location = (reference: TransitLocationReference) => {
      const key = JSON.stringify(reference);
      if (!locationCache.has(key)) {
        locationCache.set(key, resolveLocation(reference));
      }
      return locationCache.get(key)!;
    };
    try {
      const [readyMap, derived] = await Promise.all([
        mapInitialized(),
        buildMilestones(next, app.gt(), { poi, location }),
      ]);
      if (disposed || request !== generation) {
        return;
      }
      attachMap(map ?? readyMap, true);
      removeMarkers();
      replacementStarted = true;
      payload = { ...next, insets: latestInsets };
      milestones = derived;
      if (!active) {
        active = true;
        await app.bus.send('map/setNavigationMarkersVisible', { visible: false });
      }
      if (disposed || request !== generation) {
        return;
      }
      renderVisible();
    } catch (error) {
      if (request === generation && replacementStarted) {
        clear();
      }
      log.error('Unable to display route preview badges', error);
      scheduleCallouts();
    }
  };

  const unsubscribers = [
    app.bus.on<{ insets: PreviewInsets }>('map/updateRoutePreviewInsets', ({ insets }) => {
      latestInsets = insets;
      if (payload) {
        payload = { ...payload, insets };
        scheduleCallouts();
      }
    }),
    app.bus.on<ShowRoutePreviewPayload>('map/showRoutePreview', show),
    app.bus.on('map/clearRoutePreview', clear),
    app.bus.on('map/getRoutePreviewMilestones', () => milestones),
    app.bus.on('map/cleanMap', clear),
    app.bus.on('navigation/closeDirections', clear),
    app.bus.on<{ active: boolean }>('navigation/guidanceChanged', value => {
      if (value.active) {
        clear();
      }
    }),
    app.bus.on('venueData/loadNewVenue', clear),
    app.bus.on('app/reset', clear),
    app.bus.on('venueData/mapDataLoaded', clear),
    app.bus.on('i18n/langChanged', () => {
      if (latestRequestedPayload) {
        return show({ ...latestRequestedPayload, insets: latestInsets });
      }
    }),
  ];
  const styleChanged = () => {
    if (payload) {
      hideNavBadges();
    }
  };
  const detachMap = () => {
    map?.off('zoom', resizeBadges);
    map?.off('moveend', scheduleCallouts);
    map?.off('move', scheduleVisibility);
    map?.off('resize', scheduleCallouts);
    map?.off('styledata', styleChanged);
    map?.off('remove', mapRemoved);
    map = undefined;
  };
  const mapRemoved = () => {
    clear();
    detachMap();
  };
  const attachMap = (readyMap: Awaited<ReturnType<MapInitialized>>, preserveRequest = false) => {
    if (disposed || map === readyMap) {
      return;
    }
    if (map) {
      if (!preserveRequest) {
        clear();
      } else {
        removeMarkers();
        restoreNavBadges();
      }
      detachMap();
    }
    map = readyMap;
    map.on('zoom', resizeBadges);
    map.on('moveend', scheduleCallouts);
    map.on('move', scheduleVisibility);
    map.on('resize', scheduleCallouts);
    map.on('styledata', styleChanged);
    map.on('remove', mapRemoved);
  };
  void mapInitialized().then(readyMap => {
    if (!map) {
      attachMap(readyMap);
    }
  });
  const dispose = () => {
    disposed = true;
    clear();
    unsubscribers.forEach(unsubscribe => unsubscribe());
    detachMap();
  };
  return { dispose, onMapInitialized: attachMap };
}
